import * as Sentry from '@sentry/nextjs';
import Layout from 'components/layout';
import LettersPage from 'components/pages/letters/LettersPage';
import useLoadMoreLetters from 'hooks/themis/useLoadMoreLetters';
import shareImage from 'images/shares/trending.png';
import {
  getLetterCountsPerState,
  getRecentLetters,
} from 'lib/api/themis/queries/letters';
import Head from 'next/head';
import PropTypes from 'prop-types';
import { DATA_TYPES } from 'siteconfig';
import { themisResponsetoLetters } from 'utilities/letters';
import { OpenLetterTypeList } from 'utilities/propTypes';

const LETTERS_TO_REQUEST = 25; // Initial load sets the page size for later paginated requests

const Letters = ({ openLetters, stateTotals, initialNextToken }) => {
  const { letters, isLoadingMore, loadMoreLetters, loadingError, nextToken } =
    useLoadMoreLetters(openLetters, initialNextToken);

  return (
    <Layout
      title="Open Letters to Elected Officials | Resistbot"
      description="Read open letters sent to Congress, governors, and local officials by Resistbot members. Sign a petition to add your voice."
      image={shareImage}
      imageAltText='A stylized image of a woman with a megaphone shouting, over the words "we can do it"'
      canonicalPath={`/${DATA_TYPES.LETTER.basePath}`}
    >
      <Head>
        <link
          rel="alternate"
          type="application/rss+xml"
          title="Resistbot Letters RSS Feed"
          href="/letters/feed.xml"
        />
        <link
          rel="alternate"
          type="application/atom+xml"
          title="Resistbot Letters Atom Feed"
          href="/letters/feed.atom"
        />
        <link
          rel="alternate"
          type="application/json"
          title="Resistbot Letters JSON Feed"
          href="/letters/feed.json"
        />
      </Head>
      <LettersPage
        openLetters={letters}
        stateTotals={stateTotals}
        moreLettersCallback={loadMoreLetters}
        isLoadingMore={isLoadingMore}
        hasMore={!!nextToken}
      />

      {/* This is a bit rough. Duplicated in letters/explore/statecode */}
      {loadingError ? (
        <div className="flex justify-center">
          <p className="text-red-500">
            Error loading more letters: {loadingError}
          </p>
        </div>
      ) : null}
    </Layout>
  );
};

// NOTE: We have to load this live so that each user gets an individual token
// The way Timestream works, a token is used-up on each call so it can't be reused.
export const getServerSideProps = Sentry.wrapGetServerSidePropsWithSentry(
  async () => {
    const [stateTotals, recentLetters] = await Promise.all([
      getLetterCountsPerState(),
      getRecentLetters({ limit: LETTERS_TO_REQUEST }),
    ]);

    const lettersForList = themisResponsetoLetters(recentLetters.letters);

    return {
      props: {
        openLetters: lettersForList,
        initialNextToken: recentLetters.nextToken || '',
        stateTotals,
      },
    };
  }
);

Letters.propTypes = {
  openLetters: PropTypes.arrayOf(OpenLetterTypeList),
  stateTotals: PropTypes.object,
  initialNextToken: PropTypes.string,
};

export default Letters;
